import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BrainCircuit,
  Award,
  Bot,
  BookOpen,
  Check,
  ChevronRight,
  Copy,
  Crown,
  Gift,
  LineChart,
  Medal,
  Trophy,
  User,
} from "lucide-react";
import { fmtMYT, fmtMYTDate } from "@/lib/myt";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
const pick = (variants) => variants[dayOfYear % variants.length];

const NEW_TRADER_MESSAGES = [
  (f, n) => `Welcome to PlusMaker, ${f}. Your journey to ${n} begins here — complete your first milestone and start your climb.`,
  (f, n) => `${f}, every legend started exactly where you are. Your first target: ${n}. Let's begin.`,
];

const COMEBACK_MESSAGES = [
  (f, h) => `Reward claimed, ${f} — congratulations on reaching ${h}. Your next journey starts now; climb again and chase even greater rewards.`,
  (f, h) => `${h} achieved and rewarded, ${f}. A fresh climb begins — the next tier is waiting for you.`,
];

const ACTIVE_MESSAGES = [
  (f, n) => `You're making progress, ${f}. Keep pushing toward ${n} and unlock your next achievement.`,
  (f, n) => `${f}, your next milestone is ${n}. Stay consistent — prestige is earned one step at a time.`,
  (f, n) => `Momentum looks good on you, ${f}. ${n} is within reach — keep climbing.`,
];

function welcomeMessage(firstName, rank, highestRank) {
  const current = rank?.current;
  if (!current || current.level === 0) {
    if (highestRank?.name) {
      return pick(COMEBACK_MESSAGES)(firstName, highestRank.name);
    }
    return pick(NEW_TRADER_MESSAGES)(firstName, rank?.next?.name || "Rookie Trader");
  }
  if (!rank?.next) {
    return `${firstName}, you stand at the summit — PlusMaker Legend. The entire PlusMaker ecosystem salutes you.`;
  }
  return pick(ACTIVE_MESSAGES)(firstName, rank.next.name);
}

function welcomeState(rank, highestRank) {
  const current = rank?.current;
  if (!current || current.level === 0) return highestRank?.name ? "comeback" : "new";
  return rank?.next ? "active" : "legend";
}

/* Presentation-only motion layer for this dashboard. Long stillness, brief elegant
   sweep, zero logic impact. CSS-only: transform/opacity, reduced-motion safe. */
const DASH_CSS = `
  @keyframes pmMsgSweep {
    0% { background-position: 120% 0; text-shadow: 0 0 0 rgba(212,175,55,0); }
    10% { text-shadow: 0 0 22px rgba(212,175,55,.5); }
    24% { background-position: -20% 0; text-shadow: 0 0 0 rgba(212,175,55,0); }
    100% { background-position: -20% 0; text-shadow: 0 0 0 rgba(212,175,55,0); }
  }
  @supports ((-webkit-background-clip: text) or (background-clip: text)) {
    .pm-msg-shimmer {
      background: linear-gradient(100deg, #94A3B8 36%, #FFE9A8 47%, #F5D76E 52%, #94A3B8 64%);
      background-size: 260% 100%;
      background-position: 120% 0;
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: pmMsgSweep 9s ease-in-out infinite;
    }
  }
  @keyframes pmBarSweep {
    0% { transform: translateX(-115%); opacity: 0; }
    5% { opacity: 1; }
    24% { transform: translateX(115%); opacity: 1; }
    28% { opacity: 0; }
    100% { transform: translateX(115%); opacity: 0; }
  }
  @keyframes pmBarGlow {
    0%, 28%, 100% { box-shadow: 0 0 10px rgba(212,175,55,.3); }
    12% { box-shadow: 0 0 24px rgba(212,175,55,.65); }
  }
  @keyframes pmTipPulse {
    0%, 28%, 100% { opacity: .5; box-shadow: 0 0 5px 2px rgba(255,233,168,.4); }
    12% { opacity: 1; box-shadow: 0 0 15px 5px rgba(255,233,168,.85); }
  }
  .pm-progress-fill {
    position: relative;
    overflow: hidden;
    box-shadow: 0 0 10px rgba(212,175,55,.3);
    animation: pmBarGlow 6.5s ease-in-out infinite;
  }
  .pm-progress-shimmer {
    position: absolute;
    inset: 0;
    background: linear-gradient(100deg, transparent 12%, rgba(255,240,200,.85) 50%, transparent 88%);
    transform: translateX(-115%);
    opacity: 0;
    animation: pmBarSweep 6.5s ease-in-out infinite;
    pointer-events: none;
  }
  .pm-progress-fill::after {
    content: "";
    position: absolute;
    right: 1px;
    top: 50%;
    width: 7px;
    height: 7px;
    border-radius: 9999px;
    background: #FFE9A8;
    transform: translateY(-50%);
    animation: pmTipPulse 6.5s ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes pmLivePulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(52, 211, 153, .45); }
    50% { opacity: .6; box-shadow: 0 0 0 4px rgba(52, 211, 153, 0); }
  }
  .pm-live-dot { animation: pmLivePulse 2.4s ease-in-out infinite; }

  /* ---- Mobile dashboard motion (GPU-friendly: transform/opacity/box-shadow) ---- */
  @keyframes pmRankGlow {
    0%, 100% { box-shadow: 0 0 0 1px rgba(212,175,55,.55), 0 0 16px rgba(212,175,55,.10); }
    50% { box-shadow: 0 0 0 1px rgba(212,175,55,.85), 0 0 32px rgba(212,175,55,.28); }
  }
  .pm-rank-glow { animation: pmRankGlow 4.5s ease-in-out infinite; }
  /* Current Rank — complete continuous gold ring + animated perimeter halo (needs overflow:visible on card) */
  .pm-rank-halo { position: relative; overflow: visible; }
  .pm-rank-halo::before {
    content: "";
    position: absolute;
    inset: -1px;
    border-radius: 0.8125rem;
    border: 1px solid rgba(212,175,55,.85);
    box-shadow: 0 0 10px 0 rgba(212,175,55,.26), inset 0 0 10px rgba(212,175,55,.10);
    pointer-events: none;
    animation: pmRankHalo 4.6s ease-in-out infinite;
  }
  @keyframes pmRankHalo {
    0%, 100% { box-shadow: 0 0 7px 0 rgba(212,175,55,.20), inset 0 0 7px rgba(212,175,55,.08); }
    50% { box-shadow: 0 0 15px 0 rgba(212,175,55,.42), inset 0 0 12px rgba(212,175,55,.15); }
  }
  @keyframes pmBorderShimmer {
    0% { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }
  .pm-shimmer-border::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(110deg, rgba(212,175,55,.22) 0%, rgba(255,233,168,.9) 25%, rgba(212,175,55,.22) 50%, rgba(255,233,168,.9) 75%, rgba(212,175,55,.22) 100%);
    background-size: 200% 100%;
    animation: pmBorderShimmer 4.5s linear infinite;
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
            mask-composite: exclude;
    pointer-events: none;
  }
  @keyframes pmIconBreath {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.09); }
  }
  .pm-icon-breath { animation: pmIconBreath 3.8s ease-in-out infinite; }
  @keyframes pmStatusPulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(52,211,153,.5); }
    50% { opacity: .55; box-shadow: 0 0 0 5px rgba(52,211,153,0); }
  }
  .pm-status-dot { animation: pmStatusPulse 2.2s ease-in-out infinite; }
  @keyframes pmBgDrift {
    0%, 100% { transform: translate(0,0); opacity: .5; }
    50% { transform: translate(10px,-8px); opacity: .85; }
  }
  .pm-bg-drift { animation: pmBgDrift 9s ease-in-out infinite; }
  .pm-chevron { transition: transform .25s ease; }
  .pm-tap:active .pm-chevron { transform: translateX(4px); }

  /* Welcome-card corner monogram ornament (decorative, non-layout; sits behind text) */
  .pm-hero-deco { position: absolute; top: 8px; right: 8px; width: 72px; height: 72px; pointer-events: none; z-index: -1; }
  .pm-hero-glow {
    position: absolute; inset: 0; border-radius: 9999px;
    background: radial-gradient(circle at 62% 38%, rgba(212,175,55,.38), rgba(212,175,55,.08) 46%, transparent 70%);
    animation: pmHeroGlow 5s ease-in-out infinite;
  }
  @keyframes pmHeroGlow { 0%,100% { opacity:.5; transform:scale(.92);} 50% { opacity:.9; transform:scale(1.05);} }
  .pm-hero-orbit { position:absolute; inset:4px; transform-origin:50% 50%; opacity:.7; animation: pmHeroOrbit 18s linear infinite; }
  @keyframes pmHeroOrbit { from { transform:rotate(0deg);} to { transform:rotate(360deg);} }
  .pm-hero-mono { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); transform-origin:center; animation: pmHeroMono 4.2s ease-in-out infinite; }
  @keyframes pmHeroMono { 0%,100% { transform:translate(-50%,-50%) scale(1);} 50% { transform:translate(-50%,-50%) scale(1.06);} }
  .pm-hero-particle { position:absolute; width:3px; height:3px; border-radius:9999px; background:#FFE9A8; box-shadow:0 0 6px rgba(255,233,168,.85); opacity:0; }
  .pm-hero-particle.p1 { top:9px; right:12px; animation: pmHeroPar 4s ease-in-out infinite; }
  .pm-hero-particle.p2 { bottom:14px; right:8px; animation: pmHeroPar 5.2s ease-in-out .8s infinite; }
  .pm-hero-particle.p3 { top:26px; right:3px; animation: pmHeroPar 4.6s ease-in-out 1.6s infinite; }
  @keyframes pmHeroPar { 0%,100% { opacity:0; transform:translateY(2px);} 45% { opacity:.95;} 50% { transform:translateY(-2px);} 55% { opacity:.95;} }
  @media (prefers-reduced-motion: reduce) {
    .pm-msg-shimmer, .pm-progress-shimmer, .pm-progress-fill, .pm-progress-fill::after, .pm-live-dot,
    .pm-rank-glow, .pm-rank-halo::before, .pm-shimmer-border::before, .pm-icon-breath, .pm-status-dot, .pm-bg-drift, .pm-chevron,
    .pm-hero-glow, .pm-hero-orbit, .pm-hero-mono, .pm-hero-particle { animation: none !important; }
    .pm-tap:active .pm-chevron { transform: none; }
    .pm-msg-shimmer { background: none; -webkit-text-fill-color: inherit; }
    .pm-rank-glow { box-shadow: 0 0 0 1px rgba(212,175,55,.7); }
    .pm-rank-halo::before { box-shadow: 0 0 12px 1px rgba(212,175,55,.26); }
    .pm-hero-glow { opacity: .55; transform: scale(1); }
    .pm-hero-particle { opacity: .5; }
  }
`;

export default function MemberDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [eaClaims, setEaClaims] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/member/profile").then((res) => setProfile(res.data)).catch(() => {});
    api.get("/member/ea-claims").then((res) => setEaClaims(res.data)).catch(() => {});
    api.get("/member/indicators").then((res) => setIndicators(res.data)).catch(() => {});
  }, []);

  const copyUid = async () => {
    if (!profile?.user?.uid) return;
    try {
      await navigator.clipboard.writeText(profile.user.uid);
      setCopied(true);
      toast.success("Member UID copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  if (!profile) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="member-dashboard-loading">
        <div className="card-lux h-44" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card-lux h-32" />
          ))}
        </div>
      </div>
    );
  }

  const { rank, indicator_access, sponsor, highest_rank } = profile;

  // Dynamic access cards — real claimed data only.
  // EA: permanent once approved (paused subscription access is not shown as active).
  // Indicator: expiry-aware — >7 days ACTIVE, <=7 days EXPIRE SOON, expired EXPIRED.
  const activeEA = (eaClaims || []).find((c) => c.status === "approved" && c.access_status !== "paused");
  const activeIndicator = [...(indicators || [])].sort((a, b) => new Date(b.granted_at) - new Date(a.granted_at))[0];
  let indicatorState = "none";
  if (activeIndicator) {
    if (!activeIndicator.expires_at) indicatorState = "active";
    else {
      const daysLeft = (new Date(activeIndicator.expires_at).getTime() - Date.now()) / 86400000;
      indicatorState = daysLeft > 7 ? "active" : daysLeft >= 0 ? "expire_soon" : "expired";
    }
  } else if (indicator_access?.status === "expired") {
    indicatorState = "expired";
  }
  const IND_BADGE = {
    active: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10",
    expire_soon: "text-amber-300 border-amber-400/40 bg-amber-400/10",
    expired: "text-red-400/80 border-red-400/30 bg-red-400/10",
    none: "text-[#64748B] border-white/10 bg-white/5",
  };
  const badgeCls = (kind) => `text-[9px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border ${IND_BADGE[kind]}`;

  const quickLinks = [
    { to: "/member/rewards", icon: Trophy, label: "Reward Mall", desc: "Browse & claim premium rewards", soon: false },
    { to: "/member/indicators", icon: LineChart, label: "Indicators", desc: "Your assigned indicators", soon: false },
    { to: "/member/ai", icon: BrainCircuit, label: "PlusMaker AI", desc: "AI market analyst", comingSoon: true },
    { to: "/member/profile", icon: User, label: "Profile", desc: "Account details & sponsor", soon: false },
  ];

  return (
    <div data-testid="member-dashboard">
      <style>{DASH_CSS}</style>

      {/* ============ MOBILE LAYOUT (<768px) — mobile web only ============ */}
      <div className="md:hidden -mx-4 px-4 space-y-4 overflow-x-hidden" data-testid="member-dashboard-mobile">
        {/* Hero */}
        <div className="relative isolate overflow-hidden p-5 rounded-xl bg-[#050506] border-0 shadow-none" data-testid="member-mobile-hero">
          <div className="pm-hero-deco" aria-hidden="true" data-testid="member-mobile-hero-monogram">
            <span className="pm-hero-glow" />
            <svg className="pm-hero-orbit" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="27" stroke="#D4AF37" strokeWidth="1" strokeOpacity="0.55" strokeDasharray="34 120" strokeLinecap="round" />
            </svg>
            <span className="pm-hero-particle p1" />
            <span className="pm-hero-particle p2" />
            <span className="pm-hero-particle p3" />
            <span className="pm-hero-mono">
              <img
                src="/assets/pm-hero-logo.png"
                alt=""
                className="h-8 w-14 max-w-none object-contain drop-shadow-[0_0_10px_rgba(212,175,55,0.45)]"
              />
            </span>
          </div>
          <p className="eyebrow-label mb-2 text-[10px]">Member Portal</p>
          <h1 className="font-heading text-2xl font-bold text-white leading-tight" data-testid="member-mobile-welcome">
            Welcome, {user?.full_name?.split(" ")[0]}
          </h1>
          <p
            className="pm-msg-shimmer mt-3 text-sm leading-relaxed text-[#94A3B8]"
            data-testid="member-mobile-welcome-message"
            data-message-state={welcomeState(rank, highest_rank)}
          >
            {welcomeMessage(user?.full_name?.split(" ")[0] || "Trader", rank, highest_rank)}
          </p>
        </div>

        {/* Current Rank — PRIMARY HIGHLIGHT (gold outline + animated glow) */}
        <div className="pm-rank-halo card-lux relative p-5" data-testid="member-mobile-rank">
          <p className="eyebrow-label mb-3 text-[10px]">Current Rank</p>
          <div className="flex gap-3">
            <div className="pm-icon-breath h-12 w-12 shrink-0 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
              <Trophy className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-xl font-bold text-gold-gradient truncate" data-testid="member-mobile-rank-name">
                {rank?.current?.name}
              </p>
              <p className="text-xs text-[#94A3B8] mt-0.5">
                {rank?.next ? `${rank.progress_pct}% to ${rank.next.name}` : (rank?.current?.level ? `Level ${rank.current.level} / 36` : "Starting status")}
              </p>
            </div>
            <div className="w-24 shrink-0 border-l border-[#222530] pl-3" data-testid="member-mobile-next-rank">
              <p className="text-[9px] font-mono tracking-widest uppercase text-[#64748B]">Next Rank</p>
              {rank?.next ? (
                <>
                  <p className="font-heading text-sm font-bold text-[#D4AF37] mt-1 leading-tight" data-testid="member-mobile-next-rank-name">
                    {rank.next.name}
                  </p>
                  <p className="text-[10px] text-[#64748B] leading-snug mt-1.5">Continue your journey to the next level.</p>
                </>
              ) : (
                <p className="font-heading text-sm font-bold text-[#D4AF37] mt-1 leading-tight" data-testid="member-mobile-next-rank-name">
                  Highest Rank Achieved
                </p>
              )}
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-[#0B0C10] border border-[#222530] overflow-hidden">
              <div
                className="pm-progress-fill h-full rounded-full bg-gradient-to-r from-[#9C7C28] via-[#D4AF37] to-[#E5C158] transition-all duration-700"
                style={{ width: `${rank?.next ? rank.progress_pct : 100}%` }}
                data-testid="member-mobile-rank-bar"
              >
                <div className="pm-progress-shimmer" aria-hidden="true" />
              </div>
            </div>
            <span className="text-xs font-mono text-[#D4AF37] shrink-0" data-testid="member-mobile-rank-pct">
              {rank?.next ? `${rank.progress_pct}%` : "100%"}
            </span>
          </div>
        </div>

        {/* Highest Rank — Season status card (subtle gold accent, non-interactive) */}
        <div className="card-lux relative overflow-hidden p-5 border-[#D4AF37]/25" data-testid="member-mobile-highest">
          <div className="flex items-center gap-4">
            <div className="pm-icon-breath h-12 w-12 shrink-0 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
              <Crown className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Highest Rank</p>
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]" data-testid="member-mobile-highest-season">
                Season {highest_rank?.season}
              </p>
              <p className="font-heading text-lg font-bold text-white mt-0.5 truncate" data-testid="member-mobile-highest-name">
                {highest_rank?.name || "No Rank Achieved Yet"}
              </p>
            </div>
            <ChevronRight className="pm-chevron h-5 w-5 text-[#64748B]/60 shrink-0" aria-hidden="true" />
          </div>
        </div>

        {/* Primary feature cards — Reward Mall + Live & Learn (highlighted, shimmer border) */}
        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/member/rewards"
            data-testid="member-mobile-reward-mall"
            className="pm-shimmer-border pm-tap card-lux relative overflow-hidden p-4 border-[#D4AF37]/60 min-h-[150px] flex flex-col"
          >
            <div className="flex items-start justify-between">
              <div className="pm-icon-breath h-11 w-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
                <Trophy className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <ChevronRight className="pm-chevron h-4 w-4 text-[#D4AF37]" aria-hidden="true" />
            </div>
            <p className="mt-auto pt-4 font-heading text-base font-bold text-white leading-tight">Reward Mall</p>
            <p className="mt-1 text-xs text-[#94A3B8] leading-snug">Browse &amp; claim premium rewards</p>
          </Link>
          <Link
            to="/member/live-learn"
            data-testid="member-mobile-live-learn"
            className="pm-shimmer-border pm-tap card-lux relative overflow-hidden p-4 border-[#D4AF37]/60 min-h-[150px] flex flex-col"
          >
            <div className="flex items-start justify-between">
              <div className="pm-icon-breath h-11 w-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <ChevronRight className="pm-chevron h-4 w-4 text-[#D4AF37]" aria-hidden="true" />
            </div>
            <p className="mt-auto pt-4 font-heading text-base font-bold text-white leading-tight">Live &amp; Learn</p>
            <p className="mt-1 text-xs text-[#94A3B8] leading-snug">Education and market insights</p>
          </Link>
        </div>

        {/* Secondary feature cards */}
        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/member/indicators"
            data-testid="member-mobile-indicators"
            className="pm-tap card-lux p-4 flex items-center gap-3 min-h-[76px]"
          >
            <div className="h-11 w-11 shrink-0 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
              <LineChart className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white leading-tight">Indicators</p>
              <p className="text-[11px] text-[#64748B] leading-snug mt-0.5">Your assigned indicators</p>
            </div>
            <ChevronRight className="pm-chevron h-4 w-4 text-[#64748B] shrink-0" aria-hidden="true" />
          </Link>

          {/* PLUSMAKER AI — Coming Soon, visually disabled, NOT interactive */}
          <div
            data-testid="member-mobile-ai"
            aria-disabled="true"
            className="card-lux p-4 flex items-center gap-3 min-h-[76px] opacity-60"
          >
            <div className="h-11 w-11 shrink-0 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
              <BrainCircuit className="h-5 w-5 text-[#64748B]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#94A3B8] leading-tight">PlusMaker AI</p>
              <p className="text-[11px] text-[#64748B] leading-snug mt-0.5">AI market analyst</p>
              <span
                className="mt-1 inline-block text-[9px] font-mono tracking-widest uppercase text-emerald-400/80 border border-emerald-400/30 rounded-full px-2 py-0.5"
                data-testid="member-mobile-ai-coming-soon"
              >
                Coming Soon
              </span>
            </div>
          </div>

          <Link
            to="/member/lucky-draw"
            data-testid="member-mobile-lucky-draw"
            className="pm-tap card-lux p-4 flex items-center gap-3 min-h-[76px]"
          >
            <div className="h-11 w-11 shrink-0 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
              <Gift className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white leading-tight">Lucky Draw</p>
              <p className="text-[11px] text-[#64748B] leading-snug mt-0.5">Try your luck</p>
            </div>
            <ChevronRight className="pm-chevron h-4 w-4 text-[#64748B] shrink-0" aria-hidden="true" />
          </Link>

          {/* Introducing Broker — member data, non-interactive (no route exists) */}
          <div data-testid="member-mobile-ib" className="card-lux p-4 flex items-center gap-3 min-h-[76px]">
            <div className="h-11 w-11 shrink-0 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
              <User className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37]/80 leading-tight">Introducing Broker</p>
              <p className="text-sm font-bold text-white truncate mt-0.5" data-testid="member-mobile-ib-name">
                {sponsor ? (sponsor.ib_name || "PLUSMAKER IB") : "No sponsor"}
              </p>
            </div>
          </div>
        </div>

        <p className="pt-1 pb-[env(safe-area-inset-bottom)] text-center text-[10px] font-mono tracking-[0.32em] uppercase text-[#64748B]/60">
          Trade &nbsp;Learn &nbsp;Grow &nbsp;Together
        </p>
      </div>

      {/* ============ DESKTOP / TABLET LAYOUT (>=768px) — unchanged ============ */}
      <div className="hidden md:block space-y-8">
      {/* Welcome */}
      <div className="card-lux relative overflow-hidden p-8 sm:p-10">
        <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <p className="eyebrow-label mb-3">Member Portal</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="member-dashboard-welcome-heading">
          Welcome, {user?.full_name?.split(" ")[0]}
        </h1>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 text-xs font-mono tracking-widest text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full pl-3 pr-1.5 py-1.5" data-testid="member-uid-badge">
            UID {profile.user.uid || "—"}
            <button
              onClick={copyUid}
              className="text-[#D4AF37]/70 hover:text-[#D4AF37] p-1"
              data-testid="member-uid-copy-button"
              aria-label="Copy member UID"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </span>
          <span className="text-[10px] font-mono tracking-widest uppercase text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-3 py-1.5" data-testid="member-status-badge">
            {profile.user.status}
          </span>
        </div>
        <p
          className="pm-msg-shimmer mt-4 max-w-xl text-sm leading-relaxed text-[#94A3B8]"
          data-testid="member-welcome-message"
          data-message-state={welcomeState(rank, highest_rank)}
        >
          {welcomeMessage(user?.full_name?.split(" ")[0] || "Trader", rank, highest_rank)}
        </p>
      </div>

      {/* Current Rank — full-width premium (same real data, same testids) */}
      <div className="card-lux relative overflow-hidden p-8" data-testid="member-rank-card">
        <div className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow-label mb-2">Current Rank</p>
            <p className="font-heading text-2xl sm:text-3xl font-bold text-gold-gradient" data-testid="member-rank-name">
              {rank?.current?.name}
            </p>
            <p className="text-xs text-[#64748B] mt-2 font-mono">
              {rank?.current?.level ? `Level ${rank.current.level} / 36` : "Starting status"}
            </p>
          </div>
          <div className="h-12 w-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
            <Medal className="h-5 w-5 text-[#D4AF37]" />
          </div>
        </div>
      </div>

      {/* Progress to next rank */}
      <div className="card-lux p-8" data-testid="member-rank-progress-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <p className="eyebrow-label">Progress to Next Rank</p>
          <p className="font-mono text-xs text-[#94A3B8]" data-testid="member-rank-progress-text">
            {rank?.next
              ? `${rank.current.name} → ${rank.next.name} · ${rank.progress_pct}%`
              : `${rank?.current?.name} · Highest rank achieved`}
          </p>
        </div>
        <div className="h-2.5 rounded-full bg-[#0B0C10] border border-[#222530] overflow-hidden">
          <div
            className="pm-progress-fill h-full rounded-full bg-gradient-to-r from-[#9C7C28] via-[#D4AF37] to-[#E5C158] transition-all duration-700"
            style={{ width: `${rank?.next ? rank.progress_pct : 100}%` }}
            data-testid="member-rank-progress-bar"
          >
            <div className="pm-progress-shimmer" aria-hidden="true" />
          </div>
        </div>
        {rank?.current?.level === 0 && rank?.next && (
          <p className="mt-3 text-xs text-[#D4AF37]" data-testid="member-new-trader-welcome">
            Your journey to {rank.next.name} begins here.
          </p>
        )}
        {rank?.next && (
          <p className="mt-3 text-xs text-[#64748B]">
            You are at <span className="font-mono text-[#D4AF37]">{rank.current.level ? `Level ${rank.current.level} of 36` : "New Trader"}</span>.
            Next: {rank.next.name} — Level {rank.next.level}.
          </p>
        )}
      </div>

      {/* Highest Rank Achievement — season-based, system-controlled */}
      <div className="card-lux relative overflow-hidden p-8" data-testid="member-highest-rank-card">
        <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow-label mb-2">Your Highest Rank</p>
            <p className="text-xs font-mono tracking-widest uppercase text-[#64748B]" data-testid="member-highest-rank-season">
              Season {highest_rank?.season}
            </p>
          </div>
          <div className="h-12 w-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
            <Award className="h-5 w-5 text-[#D4AF37]" />
          </div>
        </div>
        <p className="mt-5 font-heading text-2xl sm:text-3xl font-bold text-gold-gradient" data-testid="member-highest-rank-name">
          {highest_rank?.name || "No Rank Achieved Yet"}
        </p>
        <p className="mt-2 text-xs text-[#64748B]">
          {highest_rank?.name
            ? `Highest official rank achieved this season — Level ${highest_rank.level} of 36`
            : "Achieve your first official rank to claim your place this season."}
        </p>
      </div>

      {/* Access cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="card-lux p-6" data-testid="member-indicator-card">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-[#64748B] tracking-wide">Indicator Access</p>
            <LineChart className="h-4 w-4 text-[#D4AF37]" />
          </div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <p
              className={`font-heading text-lg font-bold ${activeIndicator ? "text-white" : "text-[#64748B]"}`}
              data-testid="member-indicator-status"
            >
              {activeIndicator ? activeIndicator.name : "No Indicator"}
            </p>
            <span className={badgeCls(indicatorState)} data-testid="member-indicator-badge">
              {indicatorState === "active" ? "Active" : indicatorState === "expire_soon" ? "Expire Soon" : indicatorState === "expired" ? "Expired" : "No Access"}
            </span>
          </div>
          <p className="text-xs text-[#64748B] mt-1.5" data-testid="member-indicator-expiry">
            {activeIndicator && activeIndicator.expires_at
              ? `until ${fmtMYTDate(activeIndicator.expires_at)}`
              : activeIndicator
                ? "no expiry set"
                : indicatorState === "expired" && indicator_access?.expires_at
                  ? `expired ${fmtMYTDate(indicator_access.expires_at)}`
                  : "granted by administration"}
          </p>
        </div>

        <div className="card-lux p-6" data-testid="member-next-rank-card">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-[#64748B] tracking-wide">Next Rank</p>
            <Trophy className="h-4 w-4 text-[#D4AF37]" />
          </div>
          <p className="font-heading text-lg font-bold text-white" data-testid="member-next-rank-name">
            {rank?.next?.name || "Final Rank"}
          </p>
          <p className="text-xs text-[#64748B] mt-1.5 font-mono">
            {rank?.next ? `Level ${rank.next.level} of 36` : "100% complete"}
          </p>
        </div>

        <Link to="/member/expert-advisors" className="card-lux card-lux-hover p-6 relative overflow-hidden group block" data-testid="member-ea-access-card">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-[#64748B] tracking-wide">Expert Advisor Access</p>
            <Bot className="h-4 w-4 text-[#D4AF37]" />
          </div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <p className={`font-heading text-lg font-bold transition-colors ${activeEA ? "text-white group-hover:text-[#E5C158]" : "text-[#64748B]"}`} data-testid="member-ea-name">
              {activeEA ? activeEA.ea_name : "No EA"}
            </p>
            <span className={badgeCls(activeEA ? "active" : "none")} data-testid="member-ea-status">
              {activeEA ? "Active" : "No Access"}
            </span>
          </div>
          <p className="text-xs text-[#64748B] mt-1.5">{activeEA ? "permanent access" : "Browse & claim your EA access"}</p>
        </Link>
      </div>

      {/* Sponsor + quick links */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="card-lux p-8">
          <p className="eyebrow-label mb-4">Your Introducing Broker</p>
          {sponsor ? (
            <>
              <p className="font-heading text-lg font-semibold text-white" data-testid="member-sponsor-name">
                {sponsor.ib_name || "PLUSMAKER IB"}
              </p>
              <p className="font-mono text-sm text-[#D4AF37] tracking-widest mt-1.5" data-testid="member-sponsor-code">
                {sponsor.referral_code}
              </p>
            </>
          ) : (
            <p className="text-sm text-[#64748B]">No sponsor on record.</p>
          )}
        </div>
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
          {quickLinks.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`member-quick-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="card-lux card-lux-hover p-6 group"
            >
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center">
                  <item.icon className="h-4 w-4 text-[#D4AF37]" />
                </div>
                {item.soon && (
                  <span className="text-[9px] font-mono tracking-widest uppercase text-[#D4AF37]/80 border border-[#D4AF37]/30 rounded-full px-2 py-0.5">
                    Soon
                  </span>
                )}
                {item.comingSoon && (
                  <span className="text-[9px] font-mono tracking-widest uppercase text-[#D4AF37]/80 border border-[#D4AF37]/30 rounded-full px-2 py-0.5" data-testid="member-quick-link-coming-soon-plusmaker-ai">
                    Coming Soon
                  </span>
                )}
              </div>
              <p className="mt-4 text-sm font-semibold text-white group-hover:text-[#E5C158] transition-colors">
                {item.label}
              </p>
              <p className="mt-1 text-xs text-[#64748B]">{item.desc}</p>
            </Link>
          ))}
        </div>
      </div>
      </div>{/* end desktop/tablet layout */}

    </div>
  );
}
